import React, { useState, useEffect, useRef } from 'react';
import {
  Box, Button, Checkbox, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogTitle, Divider, FormControlLabel, FormGroup,
  IconButton, TextField, Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useFormik } from 'formik';
import reviewSchema from './reviewSchema';
import adminApi from '../../../utils/admin.api';

const ReviewDialog = ({ open, onClose, requestSummary, onDone }) => {
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [apiError, setApiError] = useState('');
  // Two-step "Reject All": first click arms, second click submits.
  const [rejectArmed, setRejectArmed] = useState(false);

  // ── Formik ────────────────────────────────────────────────────────────────────

  const formik = useFormik({
    initialValues: { approvedIds: [], reason: '' },
    validationSchema: reviewSchema,
    onSubmit: async (values, { setSubmitting }) => {
      setApiError('');
      try {
        const allIds = (detail?.requested_demos ?? []).map((d) => d.id);
        const rejectedIds = allIds.filter((id) => !values.approvedIds.includes(id));

        const payload = {
          approved_demo_ids: values.approvedIds,
          rejected_demo_ids: rejectedIds,
          ...(values.reason.trim() ? { rejection_reason: values.reason.trim() } : {}),
        };

        await adminApi.reviewRequest(requestSummary.request_id, payload);
        onDone();
        onClose();
      } catch (err) {
        setApiError(err?.response?.data?.detail || 'Failed to submit review.');
      } finally {
        setSubmitting(false);
      }
    },
  });

  // Ref so async callbacks always reach the latest formik without stale closures.
  const formikRef = useRef(formik);
  formikRef.current = formik;

  // ── Load detail on open ───────────────────────────────────────────────────────

  useEffect(() => {
    if (open && requestSummary) {
      setDetailLoading(true);
      setRejectArmed(false);
      setApiError('');
      formikRef.current.resetForm({ values: { approvedIds: [], reason: '' } });

      adminApi
        .getRequestDetail(requestSummary.request_id)
        .then(({ data }) => {
          const d = data?.data ?? null;
          setDetail(d);
          // Pre-select all demos for approval
          const ids = (d?.requested_demos ?? []).map((demo) => demo.id);
          formikRef.current.setFieldValue('approvedIds', ids);
        })
        .catch(() => setApiError('Failed to load request details.'))
        .finally(() => setDetailLoading(false));
    } else {
      setDetail(null);
    }
  }, [open, requestSummary]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Handlers ──────────────────────────────────────────────────────────────────

  const toggleApprove = (id) => {
    const current = formik.values.approvedIds;
    const next = current.includes(id)
      ? current.filter((x) => x !== id)
      : [...current, id];
    formik.setFieldValue('approvedIds', next);
    // Un-arm reject if user re-checks a demo
    if (!current.includes(id)) setRejectArmed(false);
  };

  const handleRejectAllClick = () => {
    if (!rejectArmed) {
      setRejectArmed(true);
      formik.setFieldValue('approvedIds', []);
    } else {
      formik.submitForm();
    }
  };

  // ── Derived state ─────────────────────────────────────────────────────────────

  const totalDemos = (detail?.requested_demos ?? []).length;
  const approvedCount = formik.values.approvedIds.length;
  const isPartialReject = approvedCount > 0 && approvedCount < totalDemos;
  const showReasonField = rejectArmed || isPartialReject;

  if (!requestSummary) return null;

  // ── Render ────────────────────────────────────────────────────────────────────

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pr: 1 }}>
        Review Request — {requestSummary?.user_name || requestSummary?.user_email || ''}
        <IconButton size="small" onClick={onClose} disabled={formik.isSubmitting}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '16px !important' }}>

        <Box>
          <Typography variant="subtitle2">User</Typography>
          <Typography>
            {requestSummary.user_name || '—'} ({requestSummary.user_email || '—'})
          </Typography>
        </Box>

        <Divider />

        {detailLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
            <CircularProgress size={24} />
          </Box>
        ) : (
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Requested Demos — select which to approve
            </Typography>
            <FormGroup>
              {(detail?.requested_demos ?? []).map((demo) => (
                <FormControlLabel
                  key={demo.id}
                  control={
                    <Checkbox
                      checked={formik.values.approvedIds.includes(demo.id)}
                      onChange={() => toggleApprove(demo.id)}
                      disabled={rejectArmed}
                    />
                  }
                  label={`${demo.title} (${demo.app})`}
                />
              ))}
            </FormGroup>
          </Box>
        )}

        {showReasonField && (
          <TextField
            label={rejectArmed ? 'Rejection reason (required)' : 'Partial rejection reason (optional)'}
            name="reason"
            value={formik.values.reason}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            fullWidth
            multiline
            rows={2}
            error={formik.touched.reason && Boolean(formik.errors.reason)}
            helperText={formik.touched.reason && formik.errors.reason}
          />
        )}

        {apiError && <Typography color="error">{apiError}</Typography>}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={formik.isSubmitting}>
          Cancel
        </Button>
        <Button
          color="error"
          variant="outlined"
          disabled={formik.isSubmitting || detailLoading}
          onClick={handleRejectAllClick}
        >
          {rejectArmed
            ? (formik.isSubmitting ? 'Rejecting...' : 'Confirm Reject All')
            : 'Reject All'}
        </Button>
        <Button
          variant="contained"
          disabled={approvedCount === 0 || !formik.isValid || formik.isSubmitting || detailLoading}
          onClick={formik.submitForm}
        >
          {formik.isSubmitting ? 'Submitting...' : `Approve (${approvedCount})`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ReviewDialog;
